/* src/ui/marketTicker.ts — the bottom-of-screen commodity marquee (TASKS.md 3.3).
 *
 * One chip per commodity, coloured by the sign of `trendPct`, scrolling continuously. It is
 * the clearest signal in the game that the world moves without you.
 *
 * Three things it has to get right:
 *
 * **It must not fight the frame budget.** A WebGL canvas already owns the frame. The scroll is
 * a CSS animation on the compositor rather than a per-frame JS transform, so it costs the main
 * thread nothing once started, and the chips are rebuilt only when prices actually change —
 * not on every poll, and never mid-animation.
 *
 * **It must pause on hover**, or the player can never read, let alone click, a moving chip.
 *
 * **It must respect `prefers-reduced-motion`.** Continuous horizontal scroll is a genuine
 * accessibility problem — a vestibular trigger for some people. Under that preference the
 * marquee does not animate at all and becomes a plain scrollable strip, which loses nothing:
 * the same information is there, the player just moves it themselves.
 */

import type { MarketEntry } from '../core/types.ts';
import { Component } from './component.ts';
import { el } from './dom.ts';
import { formatMoney } from './format.ts';

/** Below this the market is treated as flat — a 0.004% wobble is not a trend. */
const FLAT_EPSILON = 0.05;

function trendClass(pct: number): string {
  if (pct > FLAT_EPSILON) return 'gf-tick--up';
  if (pct < -FLAT_EPSILON) return 'gf-tick--down';
  return 'gf-tick--flat';
}

function formatPct(pct: number): string {
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(2)}%`;
}

function chip(entry: MarketEntry): HTMLElement {
  return el(
    'button',
    {
      class: `gf-tick ${trendClass(entry.trendPct)}`,
      attrs: { type: 'button', title: `${entry.name} — spread ${(entry.spread * 100).toFixed(1)}%` },
      data: { action: 'market:item', itemId: entry.key },
    },
    el('span', { class: 'gf-tick-key' }, entry.key),
    el('span', { class: 'gf-tick-price' }, formatMoney(entry.price)),
    el('span', { class: 'gf-tick-pct' }, formatPct(entry.trendPct)),
  );
}

export class MarketTicker extends Component {
  #track = el('div', { class: 'gf-ticker-track' });
  #signature = '';

  constructor() {
    super({});
  }

  protected render(): HTMLElement {
    const root = el(
      'div',
      { class: 'gf-ticker', attrs: { 'aria-label': 'Commodity market' } },
      this.#track,
    );

    this.bind(
      (s) => s?.market ?? [],
      (market) => {
        this.#paint(market);
      },
    );
    return root;
  }

  #paint(market: readonly MarketEntry[]): void {
    if (market.length === 0) return;

    /*
     * Rebuilding the track restarts the CSS animation, which reads as the marquee "jumping
     * back to the start" — so only rebuild when the rendered content would actually differ.
     * Prices move once a minute; polls arrive every four seconds.
     */
    const signature = market.map((m) => `${m.key}:${m.price}:${m.trendPct}`).join('|');
    if (signature === this.#signature) return;
    this.#signature = signature;

    /*
     * The track holds the chip list twice. A marquee that translates by exactly -50% of a
     * duplicated list is seamless: at the moment it wraps, the second copy is pixel-identical
     * to where the first began, so there is no visible jump and no gap to fill.
     */
    const once = market.map(chip);
    const twice = market.map(chip);
    this.#track.replaceChildren(...once, ...twice);
    this.#track.style.setProperty('--gf-tick-count', String(market.length));
  }
}
